(() => {
  const STORY_WIDTH = 1080;
  const STORY_HEIGHT = 1920;

  const roots = document.querySelectorAll('[data-ig-story-root]');
  if (!roots.length) return;

  const stateMap = new WeakMap();

  function normalizeUrl(url) {
    if (!url) return '';
    if (url.startsWith('//')) return window.location.protocol + url;
    return url;
  }

  function setStatus(root, message) {
    const status = root.querySelector('[data-ig-story-status]');
    if (status) status.textContent = message;
  }

  function setButtonLoading(button, isLoading) {
    if (!button) return;

    if (isLoading) {
      button.dataset.originalText = button.textContent;
      button.textContent = '画像を作成中...';
      button.disabled = true;
    } else {
      button.textContent = button.dataset.originalText || 'Instagram Storyでシェア';
      button.disabled = false;
    }
  }

  function roundedRect(ctx, x, y, width, height, radius) {
    const r = Math.min(radius, width / 2, height / 2);

    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + width - r, y);
    ctx.quadraticCurveTo(x + width, y, x + width, y + r);
    ctx.lineTo(x + width, y + height - r);
    ctx.quadraticCurveTo(x + width, y + height, x + width - r, y + height);
    ctx.lineTo(x + r, y + height);
    ctx.quadraticCurveTo(x, y + height, x, y + height - r);
    ctx.lineTo(x, y + r);
    ctx.quadraticCurveTo(x, y, x + r, y);
    ctx.closePath();
  }

  function drawImageCover(ctx, img, x, y, width, height) {
    const imageWidth = img.naturalWidth || img.width;
    const imageHeight = img.naturalHeight || img.height;

    const scale = Math.max(width / imageWidth, height / imageHeight);
    const sourceWidth = width / scale;
    const sourceHeight = height / scale;
    const sourceX = (imageWidth - sourceWidth) / 2;
    const sourceY = (imageHeight - sourceHeight) / 2;

    ctx.drawImage(
      img,
      sourceX,
      sourceY,
      sourceWidth,
      sourceHeight,
      x,
      y,
      width,
      height
    );
  }

  function ellipsizeToWidth(ctx, text, maxWidth) {
    let output = text;

    while (output.length > 0 && ctx.measureText(output + '…').width > maxWidth) {
      output = output.slice(0, -1);
    }

    return output + '…';
  }

  function getWrappedLines(ctx, text, maxWidth, maxLines) {
    const normalized = String(text || '').replace(/\s+/g, ' ').trim();
    if (!normalized) return [];

    const chars = Array.from(normalized);
    const lines = [];
    let line = '';

    for (const char of chars) {
      const testLine = line + char;

      if (ctx.measureText(testLine).width > maxWidth && line) {
        lines.push(line);
        line = char;

        if (lines.length === maxLines) {
          lines[maxLines - 1] = ellipsizeToWidth(ctx, lines[maxLines - 1], maxWidth);
          return lines;
        }
      } else {
        line = testLine;
      }
    }

    if (line) lines.push(line);

    if (lines.length > maxLines) {
      const trimmed = lines.slice(0, maxLines);
      trimmed[maxLines - 1] = ellipsizeToWidth(ctx, trimmed[maxLines - 1], maxWidth);
      return trimmed;
    }

    return lines;
  }

  function drawWrappedText(ctx, text, x, y, maxWidth, lineHeight, maxLines) {
    const lines = getWrappedLines(ctx, text, maxWidth, maxLines);

    lines.forEach((line, index) => {
      ctx.fillText(line, x, y + index * lineHeight);
    });

    return y + lines.length * lineHeight;
  }

  async function loadImageSafely(src) {
    const url = normalizeUrl(src);
    if (!url) return null;

    try {
      const response = await fetch(url, { mode: 'cors' });
      if (!response.ok) throw new Error('Image fetch failed');

      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);

      return await new Promise((resolve, reject) => {
        const img = new Image();

        img.onload = () => {
          URL.revokeObjectURL(objectUrl);
          resolve(img);
        };

        img.onerror = () => {
          URL.revokeObjectURL(objectUrl);
          reject(new Error('Image load failed'));
        };

        img.src = objectUrl;
      });
    } catch (error) {
      return null;
    }
  }

  function canvasToBlob(canvas) {
    return new Promise((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('Canvas export failed'));
        }
      }, 'image/png');
    });
  }

  async function createStoryImage(data) {
    const canvas = document.createElement('canvas');
    canvas.width = STORY_WIDTH;
    canvas.height = STORY_HEIGHT;

    const ctx = canvas.getContext('2d');

    // 背景
    const bg = ctx.createLinearGradient(0, 0, 0, STORY_HEIGHT);
    bg.addColorStop(0, '#fbf3e8');
    bg.addColorStop(1, '#e8d4bf');
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, STORY_WIDTH, STORY_HEIGHT);

    // 背景装飾
    ctx.globalAlpha = 0.22;
    ctx.fillStyle = '#ffffff';
    ctx.beginPath();
    ctx.arc(960, 220, 240, 0, Math.PI * 2);
    ctx.fill();

    ctx.fillStyle = '#b98b63';
    ctx.beginPath();
    ctx.arc(120, 1560, 260, 0, Math.PI * 2);
    ctx.fill();
    ctx.globalAlpha = 1;

    // 上部ラベル
    ctx.fillStyle = '#191919';
    ctx.font = '700 34px -apple-system, BlinkMacSystemFont, "Helvetica Neue", Arial, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText(data.shop || 'Shop', STORY_WIDTH / 2, 150);

    // カード
    const cardX = 90;
    const cardY = 260;
    const cardW = 900;
    const cardH = 1240;
    const cardR = 58;

    ctx.save();
    ctx.shadowColor = 'rgba(0, 0, 0, 0.24)';
    ctx.shadowBlur = 42;
    ctx.shadowOffsetY = 22;
    ctx.fillStyle = '#fffaf4';
    roundedRect(ctx, cardX, cardY, cardW, cardH, cardR);
    ctx.fill();
    ctx.restore();

    // 画像
    const imgX = cardX + 42;
    const imgY = cardY + 42;
    const imgW = cardW - 84;
    const imgH = 690;
    const imgR = 42;

    const img = await loadImageSafely(data.image);

    ctx.save();
    roundedRect(ctx, imgX, imgY, imgW, imgH, imgR);
    ctx.clip();

    if (img) {
      drawImageCover(ctx, img, imgX, imgY, imgW, imgH);
    } else {
      const placeholder = ctx.createLinearGradient(imgX, imgY, imgX + imgW, imgY + imgH);
      placeholder.addColorStop(0, '#d8c3ad');
      placeholder.addColorStop(1, '#a98261');
      ctx.fillStyle = placeholder;
      ctx.fillRect(imgX, imgY, imgW, imgH);
    }

    ctx.restore();

    // テキスト
    ctx.textAlign = 'left';

    const textX = cardX + 64;
    const textW = cardW - 128;
    let textY = imgY + imgH + 78;

    ctx.fillStyle = '#8a5f3f';
    ctx.font = '700 28px -apple-system, BlinkMacSystemFont, "Helvetica Neue", Arial, sans-serif';
    ctx.fillText('FEATURED STORY', textX, textY);

    textY += 76;

    ctx.fillStyle = '#171717';
    ctx.font = '800 58px -apple-system, BlinkMacSystemFont, "Helvetica Neue", Arial, sans-serif';
    textY = drawWrappedText(ctx, data.title, textX, textY, textW, 72, 4);

    textY += 34;

    if (data.description) {
      ctx.fillStyle = '#4d4a45';
      ctx.font = '400 32px -apple-system, BlinkMacSystemFont, "Helvetica Neue", Arial, sans-serif';
      textY = drawWrappedText(ctx, data.description, textX, textY, textW, 46, 2);
    }

    // 詳しく見るボタン風パーツ
    const pillW = 420;
    const pillH = 92;
    const pillX = cardX + (cardW - pillW) / 2;
    const pillY = cardY + cardH - 150;

    ctx.fillStyle = '#181818';
    roundedRect(ctx, pillX, pillY, pillW, pillH, 999);
    ctx.fill();

    ctx.fillStyle = '#ffffff';
    ctx.font = '800 34px -apple-system, BlinkMacSystemFont, "Helvetica Neue", Arial, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('詳しく見る', pillX + pillW / 2, pillY + 58);

    // 下部補足
    ctx.fillStyle = 'rgba(25, 25, 25, 0.68)';
    ctx.font = '500 26px -apple-system, BlinkMacSystemFont, "Helvetica Neue", Arial, sans-serif';
    ctx.fillText('Link stickerをこのボタンの上に配置', STORY_WIDTH / 2, 1645);

    return await canvasToBlob(canvas);
  }

  async function copyToClipboard(text) {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        return true;
      }
    } catch (error) {
      // noop
    }

    return false;
  }

  function downloadBlob(blob, filename) {
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = objectUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();

    setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
  }

  function getData(root) {
    return {
      title: root.dataset.title || document.title,
      url: root.dataset.url || window.location.href,
      image: root.dataset.image || '',
      description: root.dataset.description || '',
      shop: root.dataset.shop || ''
    };
  }

  function prepareStory(root) {
    const currentState = stateMap.get(root);
    if (currentState && currentState.promise) return currentState.promise;

    const data = getData(root);

    const promise = createStoryImage(data).then((blob) => {
      const file = new File([blob], 'instagram-story.png', { type: 'image/png' });

      stateMap.set(root, {
        data,
        blob,
        file,
        promise
      });

      return { data, blob, file };
    });

    stateMap.set(root, {
      data,
      promise
    });

    return promise;
  }

  async function handleShare(root, button) {
    const data = getData(root);

    setButtonLoading(button, true);
    setStatus(root, '画像を準備しています...');

    let prepared;

    try {
      prepared = await prepareStory(root);
    } catch (error) {
      setButtonLoading(button, false);
      setStatus(root, '画像作成に失敗しました。時間をおいて再度お試しください。');
      return;
    }

    const copied = await copyToClipboard(data.url);

    if (copied) {
      setStatus(root, 'URLをコピーしました。Instagramでは「スタンプ > リンク」に貼り付けてください。');
    } else {
      setStatus(root, '共有画面を開きます。InstagramではリンクスタンプにページURLを追加してください。');
    }

    const shareText = `${data.title}\n${data.url}`;
    const canShareFile =
      navigator.canShare &&
      navigator.canShare({
        files: [prepared.file]
      });

    try {
      if (navigator.share && canShareFile) {
        await navigator.share({
          title: data.title,
          text: shareText,
          url: data.url,
          files: [prepared.file]
        });

        setStatus(root, '共有画面を開きました。Instagramストーリーではリンクスタンプを追加してください。');
      } else if (navigator.share) {
        downloadBlob(prepared.blob, 'instagram-story.png');

        await navigator.share({
          title: data.title,
          text: shareText,
          url: data.url
        });

        setStatus(root, '画像を保存しました。Instagramストーリーに画像を追加し、リンクスタンプにURLを貼ってください。');
      } else {
        downloadBlob(prepared.blob, 'instagram-story.png');
        setStatus(root, '画像を保存しました。Instagramストーリーに画像を追加し、リンクスタンプにURLを貼ってください。');
      }
    } catch (error) {
      if (error && error.name === 'AbortError') {
        setStatus(root, '共有をキャンセルしました。');
      } else {
        downloadBlob(prepared.blob, 'instagram-story.png');
        setStatus(root, '共有画面を開けませんでした。画像を保存したので、Instagramから手動で投稿してください。');
      }
    } finally {
      setButtonLoading(button, false);
    }
  }

  roots.forEach((root) => {
    const button = root.querySelector('[data-ig-story-share]');
    if (!button) return;

    // 事前に画像生成を開始して、クリック時の待ち時間を減らす
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(() => prepareStory(root));
    } else {
      window.setTimeout(() => prepareStory(root), 700);
    }

    button.addEventListener('click', () => {
      handleShare(root, button);
    });
  });
})();